/**
 * @file usePagesPublish.ts
 * @description Страницы и места в окне «Опубликовать» (phase19.16 Р3а; SB102,
 * SB105, SB126–SB128; uiux-r3 §3.2).
 *
 * При открытии окна перечитывает `GET pages/changes` (отпечатки — те, что
 * видит человек), ставит отметки по умолчанию (открытая видимая страница с
 * правками — да, чужие и скрытые — нет) и после вида сайта публикует
 * отмеченное одной пачкой. Отказы — словами: 409 «черновик изменился» (кто и
 * когда, кнопка «Обновить»), занятый адрес, «публиковать нечего», ошибки
 * стоп-листа (перечитать проверки — галочку SB125 ставить заново).
 * Без права контента список не запрашивается (403), без права публикации —
 * пункты видны неактивными с объяснением.
 */

import { useEffect, useState } from "react";

import { useHasPermission } from "../../../auth/hooks";
import { useNotify } from "../../ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import { sitePagePublish, type SitePageCheck } from "../../../lib/api-site-page-publish";
import {
  checkedRowErrors,
  mergeRowSelection,
  publishItemsOf,
  publishRowsOf,
  type PublishPageRow,
  type RowSelection,
} from "../../../lib/site-builder/page-publish-items";
import {
  draftChangedText,
  pagePublishErrorText,
  parseDraftChanged,
  parsePageChecksBlocked,
  parseSlugTaken,
} from "../../../lib/site-builder/page-publish-errors";
import type { PublishPageTarget } from "../site-view-context";
import { notifyRedirectChange } from "../../../lib/site-seo/redirect-change";

type Outcome = "ok" | "failed" | "skipped";

export interface PagesSubmitResult {
  outcome: Outcome;
  /** Названия отмеченных страниц (для фразы итога). */
  titles: string[];
  /** Ошибки, пропущенные по галочке SB125. */
  acknowledged: number;
}

export function usePagesPublish(open: boolean, target: PublishPageTarget | null, currentUserId: string | null) {
  const notify = useNotify();
  const contentRight = useHasPermission("SHOP_MANAGE_CONTENT");
  const publishRight = useHasPermission("SHOP_PUBLISH_CONTENT");
  const canView = target ? target.canView : contentRight;
  const canPublish = target ? target.canPublish : publishRight;
  const openPageId = target?.pageId ?? null;
  const [rows, setRows] = useState<PublishPageRow[] | null>(null);
  const [selection, setSelection] = useState<Record<string, RowSelection>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  async function load(showOpenPage = false) {
    if (!canView) return;
    setLoadError(null);
    try {
      const next = publishRowsOf(await sitePagePublish.changesList(), currentUserId);
      setRows(next);
      setSelection((prev) => {
        const merged = mergeRowSelection(next, prev, openPageId);
        // Окно открыто кнопкой «Показать на сайте» (SB128): пункт и показ — отмечены.
        if (showOpenPage && openPageId && merged[openPageId]) merged[openPageId] = { checked: true, show: true };
        return merged;
      });
    } catch (err) {
      setLoadError(pagePublishErrorText(err, "Не удалось загрузить страницы с правками. Вид сайта опубликовать можно."));
      setRows([]);
    }
  }

  useEffect(() => {
    setProblem(null);
    if (!open) {
      setRows(null);
      setSelection({});
      return;
    }
    void load(target?.takeShowIntent?.() ?? false);
    // Список перечитывается только при открытии окна (и по «Обновить»).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const list = rows ?? [];
  const items = canPublish ? publishItemsOf(list, selection) : [];
  const errors: SitePageCheck[] = canPublish ? checkedRowErrors(list, selection) : [];

  function select(pageId: string, patch: Partial<RowSelection>) {
    setSelection((prev) => ({ ...prev, [pageId]: { checked: false, show: false, ...prev[pageId], ...patch } }));
  }

  async function refresh() {
    setProblem(null);
    await load();
  }

  /** Публикация отмеченного одной пачкой; `onAckForbidden` — текст сервера под кнопку. */
  async function submit(acknowledgeChecks: boolean, onAckForbidden: (text: string) => void): Promise<PagesSubmitResult> {
    const titles = list.filter((r) => selection[r.pageId]?.checked).map((r) => r.source.title);
    if (items.length === 0) return { outcome: "skipped", titles, acknowledged: 0 };
    setProblem(null);
    try {
      const res = await sitePagePublish.publishMany(items, { acknowledgeChecks });
      // Р3в: переадресации со старых адресов и снятые (адрес снова занят) — тостами.
      for (const page of res.pages) notifyRedirectChange(page.redirect, notify);
      await target?.afterPublish();
      await load();
      return { outcome: "ok", titles, acknowledged: res.acknowledged.length };
    } catch (err) {
      const changed = parseDraftChanged(err);
      const slug = parseSlugTaken(err);
      if (changed) {
        const title = list.find((r) => r.pageId === changed.pageId)?.source.title;
        setProblem(draftChangedText(changed, title));
      } else if (slug) {
        const title = list.find((r) => r.pageId === slug.pageId)?.source.title;
        setProblem(`Адрес /${slug.slug} уже у другой страницы на сайте${title ? ` — «${title}» не опубликовать` : ""}. Поменяйте адрес в настройках страницы и обновите окно.`);
      } else if (parsePageChecksBlocked(err)) {
        setProblem("Проверки изменились — посмотрите их ещё раз.");
        await load();
      } else if (err instanceof ApiError && err.code === "site_view_acknowledge_forbidden") {
        onAckForbidden(err.message || pagePublishErrorText(err, ""));
      } else setProblem(pagePublishErrorText(err, "Страницы не опубликованы — сайт остался прежним, черновик сохранён."));
      return { outcome: "failed", titles, acknowledged: 0 };
    }
  }

  return { rows: list, loading: rows === null && canView, loadError, canView, canPublish, selection, select, items, errors, problem, refresh, submit };
}

export type PagesPublishState = ReturnType<typeof usePagesPublish>;
